import {inject, injectable} from 'inversify';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {CoreDI} from '@fantasmagames/core/src/di/CoreDI';
import {
	Dimensions,
	ObjectSize,
	Position,
	ReelConstants,
	Scale
} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {Device} from '@fantasmagames/core/src/device/Device';
import {FullscreenManager} from '@fantasmagames/core/src/screen/fullscreen/FullscreenManager';
import {ResolutionManager} from '@fantasmagames/core/src/screen/ResolutionManager';
import {ViewTypes} from '@fantasmagames/slot/src/common/view/View';
import TextureAtlasRegion = PIXI.spine.core.TextureAtlasRegion;
import Slot = PIXI.spine.core.Slot;
import RegionAttachment = PIXI.spine.core.RegionAttachment;

interface LayoutPositionsMap {
	[name: string]: Position;
}

interface LayoutObjectsMap {
	[name: string]: Dimensions;
}

@injectable()
export class Layout {
	@inject(CoreDI.ResourceLoader)
	private _resources: ResourcesLoader;

	@inject(CoreDI.Device)
	protected _device: Device;

	@inject(CoreDI.FullscreenManager)
	private _fullscreenManager: FullscreenManager;

	@inject(CoreDI.ResolutionManager)
	private _resolutionManager: ResolutionManager;

	/** The relative positions of the bones.     */
	protected _layoutPositions: LayoutPositionsMap = {};
	/** The absolute positions of the bones. */
	protected _layoutAbsolutePositions: LayoutPositionsMap = {};

	/** The attachments' size. */
	protected _layoutObjects: LayoutObjectsMap = {};
	/** The attachments region texture size (same as in .atlas file). */
	protected _layoutRegionObjects: LayoutObjectsMap = {};

	protected _isLoaded: boolean = false;

	protected getViewPrefix(viewType: ViewTypes): string {
		if (viewType === ViewTypes.PORTRAIT) {
			return 'portrait_';
		}
		if (viewType === ViewTypes.LANDSCAPE) {
			return 'landscape_';
		}

		return '';
	}

	public loadLayout(layoutResource: string, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): void {
		const devicePrefix: string = this.getViewPrefix(viewType);
		const skeleton: PIXI.spine.core.SkeletonData = this._resources.getSpine(layoutResource, viewType);
		const spine: Spine = new Spine(skeleton);

		for (const bone of skeleton.bones) {
			const layoutName: string = devicePrefix + bone.name;
			if (this._layoutObjects[layoutName]) {
				if (!__PRODUCTION__) {
					console.warn(`Bone data for ${layoutName} already exists / has already been added. Ignoring.`);
				}

				continue;
			}
			this._layoutPositions[layoutName] = {x: bone.x, y: bone.y * -1};
			this._layoutAbsolutePositions[layoutName] = this.calculateAbsolutePositions(bone);

			const slot: Slot = spine.skeleton.findSlot(bone.name) as any;

			if (slot && (slot.attachment as RegionAttachment)) {
				this._layoutObjects[layoutName] = {
					width: (slot.attachment as RegionAttachment).width,
					height: (slot.attachment as RegionAttachment).height,
					scale: {x: slot.data.boneData.scaleX, y: slot.data.boneData.scaleY},
				};

				const region: TextureAtlasRegion = (slot.attachment as RegionAttachment).region as TextureAtlasRegion;
				if (region) {
					this._layoutRegionObjects[layoutName] = {width: region.width, height: region.height
						, scale: {x: 1, y: 1}};
				}
			}
		}

		this._isLoaded = true;
	}

	public getPosition(resourceName: string, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): Position {
		const devicePrefix: string = this.getViewPrefix(viewType);
		resourceName = devicePrefix + resourceName;

		return this._layoutPositions[resourceName]
			? {x: this._layoutPositions[resourceName].x, y: this._layoutPositions[resourceName].y}
			: null;
	}

	protected calculateAbsolutePositions(bone: PIXI.spine.core.BoneData): Position {
		const position = {x: bone.x, y: bone.y};
		let parent = bone.parent;

		while (parent !== null) {
			position.x += parent.x;
			position.y += parent.y;

			parent = parent.parent;
		}

		position.y *= -1;

		return position;
	}

	public getAbsolutePosition(resourceName: string, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): Position {
		const devicePrefix: string = this.getViewPrefix(viewType);
		resourceName = devicePrefix + resourceName;

		return this._layoutAbsolutePositions[resourceName]
			? {x: this._layoutAbsolutePositions[resourceName].x, y: this._layoutAbsolutePositions[resourceName].y}
			: null;
	}

	public getTextureRegionSize(resourceName: string, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): ObjectSize {
		const devicePrefix: string = this.getViewPrefix(viewType);
		resourceName = devicePrefix + resourceName;

		return this._layoutRegionObjects[resourceName]
			? {
				width: this._layoutRegionObjects[resourceName].width,
				height: this._layoutRegionObjects[resourceName].height
			}
			: null;
	}

	public getSize(resourceName: string, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): ObjectSize {
		const devicePrefix: string = this.getViewPrefix(viewType);
		resourceName = devicePrefix + resourceName;

		return this._layoutObjects[resourceName]
			? {width: this._layoutObjects[resourceName].width, height: this._layoutObjects[resourceName].height}
			: null;
	}

	public getScale(resourceName: string): Scale {
		return this._layoutObjects[resourceName]
			? {x: this._layoutObjects[resourceName].scale.x, y: this._layoutObjects[resourceName].scale.y}
			: null;
	}

	public getCenterOfScreen(viewType: ViewTypes): Position {
		if (viewType === ViewTypes.DESKTOP) {
			return {x: this._resolutionManager.halfResolution.width, y: this._resolutionManager.halfResolution.height};
		} else {
			if (viewType === ViewTypes.PORTRAIT) {
				return {
					x: this._resolutionManager.halfResolution.width,
					y: this._resolutionManager.halfResolution.height
				};
			} else {
				return {
					x: this._resolutionManager.halfResolution.height,
					y: this._resolutionManager.halfResolution.width
				};
			}
		}
	}

	public isLoaded(): boolean {
		return this._isLoaded;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/layout/Layout.ts